import {
  normalizeItineraryResourceNumericText,
  parseItineraryResourceEntries,
} from "./parser";
import type {
  ItineraryPeopleCounts,
  ItineraryResourceEntryReason,
  ItineraryResourcePreview,
  ItineraryTransportPoint,
  ResolvedItineraryResourceEntry,
  ResolveItineraryResourceEntriesRequest,
  ResolveItineraryResourceEntriesResult,
} from "./types";

/** 人数联动纯函数所需的最小资源字段。 */
export type ItineraryResourceQuantityInput = Pick<
  ItineraryResourcePreview,
  | "unit"
  | "travelerRole"
  | "isLinkedToPeopleCount"
  | "nightCount"
  | "dayCount"
  | "mealCount"
  | "sourceText"
> & {
  quantity: string | number;
};

/** 资源数量只依赖实际出行人数；teacherPays 仅属于报价分摊口径。 */
type ItineraryOperationalPeopleCounts = Pick<
  ItineraryPeopleCounts,
  "studentCount" | "teacherCount" | "guideCount" | "driverCount"
>;

/** 把字符串或数字安全转换为有限数值，非法值按 0 处理。 */
function toFiniteNumber(value: string | number | null | undefined): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

const MAX_SAFE_MONEY_CENTS = BigInt(Number.MAX_SAFE_INTEGER);

/** 把最多两位以上的普通十进制文本按 ROUND_HALF_UP 规整为整数分。 */
function decimalToCents(value: string | number | null | undefined): bigint | null {
  if (value === null || value === undefined) return null;
  const matched = String(value).trim().match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!matched) return null;
  const fraction = matched[3] ?? "";
  let cents = BigInt(matched[2]) * 100n + BigInt(`${fraction}00`.slice(0, 2));
  if ((fraction[2] ?? "0") >= "5") cents += 1n;
  if (cents > MAX_SAFE_MONEY_CENTS) return null;
  return matched[1] === "-" ? -cents : cents;
}

/** 整数分稳定格式化为两位小数，不经过浮点数。 */
function formatMoneyCents(cents: bigint): string {
  const negative = cents < 0n;
  const absolute = negative ? -cents : cents;
  return `${negative ? "-" : ""}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
}

/** 任意有符号整数除法按 ROUND_HALF_UP 取整。 */
function divideHalfUp(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n;
  const absolute = negative ? -numerator : numerator;
  const quotient = (absolute * 2n + denominator) / (denominator * 2n);
  return negative ? -quotient : quotient;
}

/** 金额统一按四舍五入规整为两位小数字符串。 */
export function normalizeItineraryResourceMoney(
  value: string | number | null | undefined,
): string {
  return formatMoneyCents(decimalToCents(value) ?? 0n);
}

/** 按角色返回实际消费人数；老师是否付费只影响报价分摊，不影响资源消耗数量。 */
function peopleForRole(
  role: string | null | undefined,
  counts: ItineraryOperationalPeopleCounts,
): number {
  switch (role) {
    case "学生":
      return toFiniteNumber(counts.studentCount);
    case "老师":
      return toFiniteNumber(counts.teacherCount);
    case "导游":
      return toFiniteNumber(counts.guideCount);
    case "司机":
      return toFiniteNumber(counts.driverCount);
    default:
      return toFiniteNumber(counts.studentCount) + toFiniteNumber(counts.teacherCount);
  }
}

/** 从原始自然语言中恢复独立的正整数餐数。 */
function mealCountFromSource(sourceText: string): number {
  const normalized = normalizeItineraryResourceNumericText(sourceText);
  return Number(normalized.match(/(?<![\d.+-])([1-9]\d*)\s*餐/)?.[1]) || 1;
}

/** 按角色人数及人晚、人餐、人天等业务因子推导最终数量。 */
export function deriveItineraryResourceQuantity(
  input: ItineraryResourceQuantityInput,
  counts: ItineraryOperationalPeopleCounts,
): number {
  if (!input.isLinkedToPeopleCount) {
    return toFiniteNumber(input.quantity);
  }
  const people = peopleForRole(input.travelerRole, counts);
  const factor = input.unit === "人晚"
    ? Math.max(toFiniteNumber(input.nightCount), 1)
    : input.unit === "人餐"
      ? toFiniteNumber(input.mealCount) > 0
        ? toFiniteNumber(input.mealCount)
        : mealCountFromSource(input.sourceText)
      : input.unit === "人天"
        ? Math.max(toFiniteNumber(input.dayCount), 1)
        : 1;
  return people * factor;
}

/** 计算行程资源金额并统一保留两位小数。 */
export function calculateItineraryResourceAmount(
  quantity: string | number,
  unitPrice: string | number,
): string {
  const quantityCents = decimalToCents(quantity);
  const unitPriceCents = decimalToCents(unitPrice);
  if (quantityCents === null || unitPriceCents === null) return "0.00";
  const amountCents = divideHalfUp(quantityCents * unitPriceCents, 100n);
  return amountCents > MAX_SAFE_MONEY_CENTS || amountCents < -MAX_SAFE_MONEY_CENTS
    ? "0.00"
    : formatMoneyCents(amountCents);
}

/** 判断未知异常是否携带调用方预先归一的结构化诊断字段。 */
function structuredCause(error: unknown): Partial<ItineraryResourceEntryReason> {
  return typeof error === "object" && error !== null
    ? error as Partial<ItineraryResourceEntryReason>
    : {};
}

/** 格式化交通候选，名称和简称不同时同时展示。 */
function transportCandidateLabel(candidate: ItineraryTransportPoint): string {
  return candidate.shortName && candidate.shortName !== candidate.name
    ? `${candidate.name}（${candidate.shortName}）`
    : candidate.name;
}

/** 校验单个大交通端点，并把查询异常归一为共享结构化错误。 */
async function validateTransportPoint(
  preview: ItineraryResourcePreview,
  field: "fromPoint" | "toPoint",
  searchTransportPoints: ResolveItineraryResourceEntriesRequest["searchTransportPoints"],
): Promise<ItineraryResourceEntryReason | null> {
  const value = preview[field];
  if (!preview.transportMode || !value) return null;
  let candidates: ItineraryTransportPoint[];
  try {
    candidates = await searchTransportPoints(preview.transportMode, value);
  } catch (error) {
    const cause = structuredCause(error);
    return {
      code: "transport_point_search_failed",
      field,
      value,
      message: typeof cause.message === "string" ? cause.message : "大交通点位查询失败",
      ...(preview.lineNumber ? { lineNumber: preview.lineNumber } : {}),
      ...(typeof cause.causeCode === "string" ? { causeCode: cause.causeCode } : {}),
      ...(typeof cause.status === "number" ? { status: cause.status } : {}),
      ...(typeof cause.traceId === "string" ? { traceId: cause.traceId } : {}),
    };
  }
  const matched = candidates.some(
    (candidate) => candidate.name === value || candidate.shortName === value,
  );
  if (matched) return null;
  return {
    code: "transport_point_not_found",
    field,
    value,
    message: `没有“${value}”${preview.transportMode === "air" ? "机场" : "火车站"}，请检查名称或从候选中重新选择`,
    ...(preview.lineNumber ? { lineNumber: preview.lineNumber } : {}),
    candidates: candidates.map(transportCandidateLabel),
  };
}

/** 把解析预览应用人数与金额口径。 */
function resolvePreview(
  preview: ItineraryResourcePreview,
  counts: ItineraryOperationalPeopleCounts,
): ResolvedItineraryResourceEntry {
  const quantity = deriveItineraryResourceQuantity(preview, counts);
  return {
    ...preview,
    quantity,
    amount: calculateItineraryResourceAmount(
      quantity,
      preview.unitPriceText ?? preview.unitPrice,
    ),
  };
}

/**
 * 原子解析行程资源：先同步解析，再并行校验全部交通端点，最后统一解析人数和金额。
 */
export async function resolveItineraryResourceEntries(
  request: ResolveItineraryResourceEntriesRequest,
): Promise<ResolveItineraryResourceEntriesResult> {
  const parsed = parseItineraryResourceEntries(request.text, {
    activeResourceType: request.activeResourceType,
    entryOptions: request.context.entryOptions,
  });
  if (!parsed.ok) return parsed;

  if (request.validateTransport !== false) {
    const checks = await Promise.all(
      parsed.previews.flatMap((preview) => (["fromPoint", "toPoint"] as const).map(
        (field) => validateTransportPoint(preview, field, request.searchTransportPoints),
      )),
    );
    const failed = checks.find((reason) => reason !== null);
    if (failed) {
      return { ok: false, error: failed.message, reason: failed };
    }
  }
  return {
    ok: true,
    entries: parsed.previews.map((preview) => resolvePreview(preview, request.context.peopleCounts)),
  };
}
